import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as APIStore from "../../store/APIStore";
import FacilityPatientsByMonth from "./FacilityPatientsByMonth";
import AdmitTypeBarChart from "./AdmitTypeBarChart";
import MUIDataTable from "mui-datatables";
import { Grid, Card, Typography } from "@material-ui/core";
import BarChartIcon from '@material-ui/icons/BarChart';
import moment from "moment";

type OwnProps = {
    month: number,
    year: number
};

const mapState = (state: ApplicationState) => state.vitalsContext;

type StateProps = ReturnType<typeof mapState>;
type DispatchProps = typeof APIStore.actionCreators;
type DrillDownPatientProps = OwnProps & StateProps & DispatchProps;

const tableColumns = [
    {
        name: "Patient",
        options: { filter: false }
    },
    {
        name: "Admit Date",
        options: { filter: false }
    },
    {
        name: "DOB",
        options: { filter: false }
    },
    {
        name: "Primary Care Provider",
        options: { filter: false }
    }
];

const tableOptions = {
    elevation: 0,
    filter: false,
    viewColumns: false,
    selectableRows: 'none' as const,
    selectableRowsOnClick: false,
    print: false,
    download: false,
    search: false,
    setRowProps: () => ({
        style: { cursor: 'default' }
    }),
};

const DrillDownPatient = (props: DrillDownPatientProps) => {
    const {
        PhysicianAdmitsStats,
        userPracticeCoordinatorProfile,
        Patients,
        requestPatientsByPracticeIdWithDays,
        APIToken,
        requestCurrentUserCareCoordinatorProfile,
        requestAdmitStatsForProvider,
        month,
        year
    } = props;

    const [tokenState, setTokenState] = useState<APIStore.TokenType | undefined>();
    const [totalAdmits, setTotalAdmits] = useState<number>(0);

    useEffect(() => {
        if (APIToken && !tokenState?.accessToken) {
            setTokenState(APIToken);
        }
    }, [APIToken, tokenState]);

    useEffect(() => {
        if (APIToken && !tokenState?.accessToken) {
            setTokenState(APIToken);
        }
    }, [APIToken, tokenState]);

    useEffect(() => {
        if (userPracticeCoordinatorProfile) {
            let { isErrored, notFound, isLoaded } = userPracticeCoordinatorProfile
            if ((!isErrored && !notFound && !isLoaded) && tokenState?.accessToken) {
                requestCurrentUserCareCoordinatorProfile(tokenState.accessToken);

            } else {
                if (isLoaded && tokenState?.accessToken) {
                    let coordinatorProfile = userPracticeCoordinatorProfile.coordinatorProfile;

                    // Update Patient List
                    if (!Patients.isLoaded && !Patients.isLoading) {
                        const startDate = moment({ year, month: month - 1, day: 1 }).format("YYYY-MM-DD");
                        requestPatientsByPracticeIdWithDays(
                            coordinatorProfile.defaultPractice.practiceId,
                            coordinatorProfile.practiceCareCoordinatorId,
                            30, //(days)
                            tokenState?.accessToken,
                            startDate
                        );
                    }
                }

            }
        }

    }, [tokenState, userPracticeCoordinatorProfile.coordinatorProfile, Patients]);

    useEffect(() => {
        // Update Admit Stats
        if (tokenState?.accessToken && !PhysicianAdmitsStats.isLoaded && !PhysicianAdmitsStats.isLoading) {
            requestAdmitStatsForProvider(tokenState.accessToken);
        }
    }, [tokenState, PhysicianAdmitsStats]);

    useEffect(() => {
        PhysicianAdmitsStats.physicianAdmits.forEach((admitMonth: APIStore.AdmitSummaryStatistics) => {
            if (admitMonth.month == month && admitMonth.year == year) {
                setTotalAdmits(admitMonth.totalAdmits);
            }
        });
    }, [tokenState]);

    const tableData = Patients?.patientsByPracticeId
        ? Patients.patientsByPracticeId.map((patient: APIStore.ListPatient) => [
            `${patient.firstName} ${patient.lastName}`,
            patient.admitDate ? moment(patient.admitDate).format("MM-DD-YYYY") : 'N/A',
            patient.dateOfBirth ? moment(patient.dateOfBirth).format("MM-DD-YYYY") : 'N/A',
            patient.primaryCareProvider
        ])
        : [];

    const isPatientsLoading = !Patients.isLoaded || Patients.isLoading;

    return (
        <Grid container direction="column" spacing={3}>
            <Grid item xs={12}>
                <Typography variant="subtitle1">Total Admissions: {totalAdmits}</Typography>
            </Grid>

            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Card elevation={3} style={{ padding: 16 }}>
                        <Typography variant="subtitle2" gutterBottom>Admission by Day</Typography>
                        <FacilityPatientsByMonth month={month} year={year} />
                    </Card>
                </Grid>
                <Grid item xs={6}>
                    <Card elevation={3} style={{ padding: 16 }}>
                        <Typography variant="subtitle2" gutterBottom>Admission Types</Typography>
                        <AdmitTypeBarChart month={month} year={year} />
                    </Card>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                {isPatientsLoading ?
                    <BarChartIcon className="animate-flicker" style={{ fontSize: 100 }} color="disabled" />
                    :
                    <MUIDataTable
                        title=""
                        data={tableData}
                        columns={tableColumns}
                        options={tableOptions}
                    />
                }
            </Grid>
        </Grid>
    )
}

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (state: ApplicationState) => state.vitalsContext,
    APIStore.actionCreators
)(DrillDownPatient as any);